import React, { useEffect, useRef, useState } from "react"
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Select,
  TextField,
  Typography,
} from "@mui/material"
import profileimage from "../../images/personal-info.svg"
import Stepper from "@mui/material/Stepper"
import Step from "@mui/material/Step"
import StepLabel from "@mui/material/StepLabel"
import * as Style from "./Style"
import Button from "@mui/material/Button"
import MenuItem from "@mui/material/MenuItem"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { GoPlusCircle } from "react-icons/go"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import { useForm } from "react-hook-form"
import { useAppSelector } from "../../app/hooks"
import {
  setLanguage,
  setResendButton,
  setStepsCount,
  setString,
} from "../../features/Auth/WrapperSlice"
import { store, useAppDispatch } from "../../app/store"
import { api, useProviderDetailsMutation } from "../../features/Auth/authApi"
import { onBoardKey, token, userDetails } from "../../features/Auth/authSlice"
import { toast } from "react-toastify"
import { useNavigate } from "react-router-dom"
import CropperModal from "../modals/CroperModal"

interface PersonalAccount {
  description: string
}
interface PersonalStepProps {
  activeStep: string
  step: string
  handleNext: () => void
  handleBack: () => void
  handleReset: () => void
}

const ProfileStep: React.FC<PersonalStepProps> = ({
  activeStep,
  step,
  handleNext,
  handleReset,
  handleBack,
}) => {
  const [createJob, { data: jobData, error: jobError, isLoading }] =
    useProviderDetailsMutation()
  const dispatch = useAppDispatch()
  const stringValue = useAppSelector((state) => state?.wrapper?.stringValue)
  const userId = useAppSelector((state) => state?.auth?.userDetails)
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [fileUpload, setFileUpload] = useState<File | null>(null)
  const [imageError, setImageError] = useState("")
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)
  const [cropperOpen, setCropperOpen] = useState(false)
  const [imageToCrop, setImageToCrop] = useState<{
    imageSrc: string
    extension: string
  }>({ imageSrc: "", extension: "png"});
  const handleBoxClick = () => {
    ;(fileInputRef.current as any)?.click()
  }

  const handleFileChange = (e: any) => {
    const file = e.target.files[0]
    if (file) {
      const maxSize = 5 * 1024 * 1024
      if (file.size > maxSize) {
        toast.error("File Size Exceeds 5MB. Please Select A Smaller File.", {
          autoClose: 1000,
        })
        return
      }

      if (!["image/jpeg", "image/png"].includes(file.type)) {
        toast.error(
          "Unsupported File Type. Please Select An Image File (JPEG or PNG).",
          { autoClose: 1000 },
        )
        return
      }

      const extension = file.name.split(".").pop().toLowerCase();
      const reader = new FileReader()
      reader.onloadend = () => {
        setImageToCrop({
          imageSrc: reader.result as string,
          extension: extension
        })
        setCropperOpen(true);
      }
      reader.readAsDataURL(file)
    }
  }

  const handleCroppedImage = (file: File) => {
    setFileUpload(file);
    setImageError("");
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as any);
    };
    reader.readAsDataURL(file);
    setCropperOpen(false);
  }

  const schemaResolver = yupResolver(
    yup.object().shape({
      description: yup
        .string()
        .required("Please Fill The Mandatory Fields")
        .min(20, "Bio Must Be At Least 20 Characters.")
        .max(500, "Bio Should Be Maximum 500 Characters"),
    }),
  )

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PersonalAccount>({
    resolver: schemaResolver,
  })

  useEffect(() => {
    if ((stringValue as any)?.description) {
      setValue("description", (stringValue as any).description)
    }
    if ((stringValue as any)?.fileUpload) {
      setFileUpload((stringValue as any).fileUpload)
    }
    if ((stringValue as any)?.imagePreview) {
      setImagePreview((stringValue as any).imagePreview)
    }
  }, [stringValue, setValue])

  const onSubmit = (value: any) => {
    if (!fileUpload) {
      toast.error("Please Fill The Mandatory Fields", { autoClose: 800 })
      setIsButtonDisabled(true)

      setTimeout(() => {
        setIsButtonDisabled(false)
      }, 1000)
      return
    }
    const formData = new FormData()

    formData.append("name", stringValue?.name)
    formData.append("email", stringValue?.email)
    formData.append("contactNo", stringValue?.contactNo)
    formData.append("gender", stringValue?.gender)
    formData.append("region", stringValue?.region)
    formData.append("address", stringValue?.address)
    formData.append("dob", stringValue?.dob)
    formData.append("services", (stringValue as any)?.services)
    formData.append("language", (stringValue as any)?.languageAll)
    formData.append("experience", (stringValue as any)?.experience)
    formData.append("description", value?.description)
    const idVal = userId?.user?._id || userId?.user?.userId || ""
    formData.append("_id", idVal)
    formData.append("profilePic", fileUpload as any)
    const signUpMethod = userId?.user?.signUpWith === "Google" ? "Google" : "Email"
    formData.append("signUpWith", signUpMethod)
    
    createJob(formData)
  }
  useEffect(() => {
      if (jobData?.message && jobData?.token) {
        dispatch(token(jobData?.token)); 
        navigate("/dashboard"); 
        toast.success(jobData?.message, { autoClose: 2000 });
        store.dispatch(api.endpoints.getNotification.initiate({ pageLimit: 3 }));
      } else if  (jobData?.message) {
      dispatch(setResendButton("resend"))
      toast.success(jobData?.message)
      dispatch(setString(null))
      dispatch(userDetails(null as any))
      dispatch(setStepsCount(null))
      dispatch(setLanguage(null))
      dispatch(onBoardKey(null as any))
      handleReset()
      navigate("/")
    }
  }, [jobData])

  useEffect(() => {
    if (errors?.description?.message) {
      toast.error(errors?.description?.message, { autoClose: 800 })
      setIsButtonDisabled(true)

      setTimeout(() => {
        setIsButtonDisabled(false)
      }, 1000)
    }
  }, [errors])

  const handleBackWithData = () => {
    dispatch(
      setString({
        ...stringValue,
        description: watch("description"),
        fileUpload: fileUpload,
        imagePreview: imagePreview,
      })
    )
    handleBack()
  }

  return (
    <Style.PersonalInfo className="p-5">
      <Box>
        <Box className="pt-3">
          <Box>
            <Typography>
              Profile Picture<span className="text-danger">*</span>
            </Typography>
          </Box>
          <Box
            className="upload-photo position-relative my-3"
            onClick={handleBoxClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: "none" }}
              onChange={handleFileChange}
              accept=".jpg,.png,.jpeg"
            />
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <GoPlusCircle
                className="upload-icon"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            )}
          </Box>
        </Box>

        <p className="text-danger">{imageError}</p>
        <Box>
          <Typography>
            Bio<span className="text-danger">*</span>
          </Typography>
        </Box>
        <form className="pt-2" onSubmit={handleSubmit(onSubmit)}>
          <textarea
            placeholder="Write Your Bio"
            style={{
              width: "100%",
              height: "100px",
              borderRadius: "10px",
              padding: "10px",
            }}
            {...register("description")}
          />

          <Button
            color="inherit"
            disabled={(activeStep as any) === 0}
            onClick={handleBackWithData}
            sx={{ mr: 1 }}
            className="next-button my-2"
          >
            Back
          </Button>
          <Button type="submit" className="next-button grey-button my-two">
            {isLoading ? (
              <CircularProgress style={{ color: "pink" }} size={24} />
            ) : (
              <>{(activeStep as any) === step.length - 1 ? "Finish" : "Next"}</>
            )}
          </Button>
        </form>
      </Box>

      <CropperModal
      open={cropperOpen}
      onClose={() => setCropperOpen(false)}
      level={1}
      data={imageToCrop}
      setResults={handleCroppedImage}
      />
    </Style.PersonalInfo>
  )
}

export default ProfileStep
